import React, { createContext, useState, useEffect, useContext, useRef } from 'react';
import api from '../api'; // Import API instance instead of axios
import { AuthContext } from './AuthContext';

export const WatchlistContext = createContext();

// Cache for watchlist check results
const watchlistCheckCache = new Map();
const CACHE_EXPIRY = 5 * 60 * 1000; // 5 minutes

export const WatchlistProvider = ({ children }) => {
  const { currentUser } = useContext(AuthContext);
  const [watchlist, setWatchlist] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const pendingChecks = useRef(new Map());

  // Clear cache when user changes
  useEffect(() => {
    watchlistCheckCache.clear();
  }, [currentUser]);

  // fetch watchlist when user changes
  useEffect(() => {
    if (currentUser) {
      fetchWatchlist();
    } else {
      setWatchlist([]);
    }
  }, [currentUser]);

  // get user's watchlist from api
  const fetchWatchlist = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.get('/watchlist');
      
      const watchlistItems = response.data.data || [];
      setWatchlist(watchlistItems);
      
      // Update cache with watchlist items for faster lookups
      watchlistItems.forEach(item => {
        const cacheKey = `${item.type}-${item.id}-${currentUser?._id || 'guest'}`;
        watchlistCheckCache.set(cacheKey, {
          inWatchlist: true,
          timestamp: Date.now()
        });
      });
    } catch (err) {
      console.error('Error fetching watchlist:', err);
      
      // Check for rate limit error
      if (err.isRateLimit) {
        setError("You've reached the rate limit. Please wait a moment before trying again.");
      } else {
        setError('Failed to load watchlist. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  const addToWatchlist = async (contentType, contentId) => {
    try {
      setLoading(true);
      setError(null);
      
      await api.post(`/watchlist/add?content_type=${contentType}&content_id=${contentId}`);
      
      // Update cache
      const cacheKey = `${contentType}-${contentId}-${currentUser?._id || 'guest'}`;
      watchlistCheckCache.set(cacheKey, {
        inWatchlist: true,
        timestamp: Date.now()
      });
      
      // Refresh
      await fetchWatchlist();
      
      return true;
    } catch (err) {
      console.error('Error adding to watchlist:', err);
      
      // Check for rate limit error
      if (err.isRateLimit) {
        setError("You've reached the rate limit. Please wait a moment before trying again.");
      } else {
        setError(err.response?.data?.detail?.message || 'Failed to add to watchlist. Please try again.');
      }
      
      return false;
    } finally {
      setLoading(false);
    }
  };

  const removeFromWatchlist = async (contentType, contentId) => {
    try {
      setLoading(true);
      setError(null);
      
      await api.delete(`/watchlist/remove?content_type=${contentType}&content_id=${contentId}`);
      
      // Update cache
      const cacheKey = `${contentType}-${contentId}-${currentUser?._id || 'guest'}`;
      watchlistCheckCache.set(cacheKey, {
        inWatchlist: false,
        timestamp: Date.now()
      });
      
      await fetchWatchlist();
      
      return true;
    } catch (err) {
      console.error('Error removing from watchlist:', err);
      
      // Check for rate limit error
      if (err.isRateLimit) {
        setError("You've reached the rate limit. Please wait a moment before trying again.");
      } else {
        setError(err.response?.data?.detail?.message || 'Failed to remove from watchlist. Please try again.');
      }
      
      return false;
    } finally {
      setLoading(false);
    }
  };

  // verify if content is in user's watchlist
  const isInWatchlist = async (contentType, contentId) => {
    // If user is not logged in, return false
    if (!currentUser) return false;
    
    try {
      const cacheKey = `${contentType}-${contentId}-${currentUser?._id || 'guest'}`;
      
      // Check if we have a cached result
      if (watchlistCheckCache.has(cacheKey)) {
        const cachedData = watchlistCheckCache.get(cacheKey);
        // Check if cache is still valid
        if (Date.now() - cachedData.timestamp < CACHE_EXPIRY) {
          return cachedData.inWatchlist;
        }
        // Cache expired, remove it
        watchlistCheckCache.delete(cacheKey);
      }
      
      // Check if there's already a pending request for this item
      if (pendingChecks.current.has(cacheKey)) {
        // Wait for the pending request to complete
        return pendingChecks.current.get(cacheKey);
      }
      
      // Create a new promise for this request
      const checkPromise = new Promise(async (resolve) => {
        try {
          const response = await api.get(`/watchlist/check?content_type=${contentType}&content_id=${contentId}`);
          const isInList = response.data.data.inWatchlist;
          
          // Cache the result
          watchlistCheckCache.set(cacheKey, {
            inWatchlist: isInList,
            timestamp: Date.now()
          });
          
          resolve(isInList);
        } catch (err) {
          console.error('Error checking watchlist:', err);
          resolve(false);
        } finally {
          // Remove from pending checks
          pendingChecks.current.delete(cacheKey);
        }
      });
      
      // Store the promise in pending checks
      pendingChecks.current.set(cacheKey, checkPromise);
      
      return checkPromise;
    } catch (err) {
      console.error('Error checking watchlist:', err);
      
      // silent check without error display
      return false;
    }
  };

  return (
    <WatchlistContext.Provider value={{
      watchlist,
      loading,
      error,
      setError,
      fetchWatchlist,
      addToWatchlist,
      removeFromWatchlist,
      isInWatchlist
    }}>
      {children}
    </WatchlistContext.Provider>
  );
};